import { useState, useEffect } from 'react';
import ApiService from '../services/api';

export const useProducts = (category, writer, publication, pageSize = 10) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    // Skip fetch if no meaningful filters (only pageSize)
    const hasFilter = category || writer || publication;
    
    if (!hasFilter) {
      setLoading(false);
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build filters object inside the effect
        const filters = {};
        if (category) filters.category = category;
        if (pageSize) filters.page_size = pageSize;

        const response = await ApiService.getProducts(filters);

        setProducts(response.results || []);
        setPagination({
          count: response.count,
          next: response.next,
          previous: response.previous,
        });
      } catch (err) {
        setError(err.message);
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, writer, publication, pageSize]);

  return { products, loading, error, pagination };
};

export const useProduct = (slugOrUuid) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slugOrUuid) {
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await ApiService.getProductBySlugOrUUID(slugOrUuid);
        
        setProduct(response);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slugOrUuid]);

  return { product, loading, error };
};

export const useCategories = (page = 1, pageSize = 20) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    totalPages: 0,
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await ApiService.getCategories({ page, page_size: pageSize });
        
        const categoriesData = Array.isArray(response) ? response : (response.results || []);

        setCategories(categoriesData);
        if (!Array.isArray(response)) {
          setPagination({
            count: response.count || 0,
            next: response.next,
            previous: response.previous,
            totalPages: Math.ceil((response.count || 0) / pageSize),
          });
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [page, pageSize]);

  return { categories, loading, error, pagination };
};

export const useCategory = (slugOrId) => {
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slugOrId) {
      setLoading(false);
      return;
    }

    const fetchCategory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await ApiService.getCategoryBySlugOrId(slugOrId);
        
        setCategory(response);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching category:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [slugOrId]);

  return { category, loading, error };
};
